// components/OptionsSelection.js
import React, { useState, useEffect } from 'react';
import { differenceInDays } from 'date-fns';
import { db } from '../services/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { 
  Loader2, Info, AlertTriangle, CheckCircle, AlertCircle,
  Coffee, Utensils, Car, Heart, Gift, Star, Package, 
  Wifi, Baby, Dog, PawPrint, Clock, Key, Home, Tv, Laptop,
  Calendar, CreditCard, Users, Shield, Mail
} from 'lucide-react';

const OptionsSelection = ({
  selectedOptions,
  setSelectedOptions,
  selectedCheckIn,
  selectedCheckOut,
  selectedOccupancy,
  selectedRoomType,
  onOptionsLoaded,
  language,
  t
}) => {
  
  const [availableOptions, setAvailableOptions] = useState([]);
  const [infoModules, setInfoModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsByCategory, setItemsByCategory] = useState({});
  
  const nights = selectedCheckIn && selectedCheckOut ? differenceInDays(selectedCheckOut, selectedCheckIn) : 1;
  const guests = selectedOccupancy[selectedRoomType] || 2;

  // Map des icônes disponibles
  const iconMap = {
    'Coffee': Coffee,
    'Utensils': Utensils,
    'Car': Car,
    'Heart': Heart,
    'Gift': Gift,
    'Star': Star,
    'Package': Package,
    'Wifi': Wifi,
    'Baby': Baby,
    'Dog': Dog,
    'PawPrint': PawPrint,
    'Clock': Clock,
    'Key': Key,
    'Home': Home,
    'Tv': Tv,
    'Laptop': Laptop,
    'Calendar': Calendar,
    'CreditCard': CreditCard,
    'Users': Users,
    'Shield': Shield,
    'Mail': Mail,
    'Info': Info,
    'AlertTriangle': AlertTriangle,
    'CheckCircle': CheckCircle,
    'AlertCircle': AlertCircle
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Package;
  };
  
  useEffect(() => {
    loadOptions();
  }, []);
  
  useEffect(() => {
    if (onOptionsLoaded) {
      onOptionsLoaded(availableOptions);
    }
  }, [availableOptions]);
  
  const loadOptions = async () => {
    try {
      setLoading(true);
      console.log('Chargement des options depuis Firebase...');
      
      const itemsSnapshot = await getDocs(
        query(
          collection(db, 'booking_options'),
          where('isActive', '==', true),
          where('showInBooking', '==', true),
          orderBy('sortOrder', 'asc')
        )
      );
      
      console.log(`${itemsSnapshot.size} éléments trouvés dans Firebase`);
      
      const itemsData = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const options = itemsData.filter(item => item.type !== 'info');
      const infos = itemsData.filter(item => item.type === 'info');
      
      console.log(`${options.length} options, ${infos.length} modules d'info`);
      
      const grouped = {};
      itemsData.forEach(item => {
        const category = item.category || 'extras';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });
      
      setItemsByCategory(grouped);
      setAvailableOptions(options);
      setInfoModules(infos);
      
    } catch (error) {
      console.error('Erreur chargement options:', error);
      try {
        const itemsSnapshot = await getDocs(
          query(
            collection(db, 'booking_options'),
            where('isActive', '==', true),
            where('showInBooking', '==', true)
          )
        );
        
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        itemsData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        
        const options = itemsData.filter(item => item.type !== 'info');
        const infos = itemsData.filter(item => item.type === 'info');
        
        const grouped = {};
        itemsData.forEach(item => {
          const category = item.category || 'extras';
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push(item);
        });
        
        setItemsByCategory(grouped);
        setAvailableOptions(options);
        setInfoModules(infos);
        
      } catch (retryError) {
        console.error('Erreur même sans orderBy:', retryError);
        setAvailableOptions([]);
        setInfoModules([]);
        setItemsByCategory({});
      }
    } finally {
      setLoading(false);
    }
  };
  
  const toggleOption = (optionId) => {
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(selectedOptions.filter(id => id !== optionId));
    } else {
      setSelectedOptions([...selectedOptions, optionId]);
    }
  };
  
  const getOptionPrice = (option) => {
    if (!option || !option.price) return 0;
    
    switch(option.unit) {
      case 'personne/jour':
        return option.price * guests * nights;
      case 'personne':
        return option.price * guests;
      case 'jour':
        return option.price * nights;
      case 'réservation':
      case 'unité':
      default:
        return option.price;
    }
  };
  
  const getOptionDescription = (option) => {
    if (!option) return '';
    const price = option.price || 0;
    
    switch(option.unit) {
      case 'personne/jour':
        return `${price}€ ${t.perPersonPerDay} (${guests} ${guests > 1 ? t.persons : t.person} × ${nights} ${nights > 1 ? t.nights : t.night})`;
      case 'personne':
        return `${price}€ ${t.perPerson} (${guests} ${guests > 1 ? t.persons : t.person})`;
      case 'jour':
        return `${price}€ ${t.perDay} (${nights} ${nights > 1 ? t.nights : t.night})`;
      case 'chambre':
        return `${price}€ ${t.perRoom}`;
      case 'réservation':
        return `${price}€ ${t.forBooking}`;
      case 'unité':
        return `${price}€`;
      default:
        return `${price}€`;
    }
  };
  
  const categoryLabels = {
    'restauration': { 
      label: language === 'fr' ? 'Restauration' : 'Dining', 
      icon: Utensils 
    },
    'bien-être': { 
      label: language === 'fr' ? 'Bien-être & Spa' : 'Wellness & Spa', 
      icon: Heart 
    },
    'transport': { 
      label: language === 'fr' ? 'Transport' : 'Transportation', 
      icon: Car 
    },
    'services': { 
      label: language === 'fr' ? 'Services' : 'Services', 
      icon: Star 
    },
    'extras': { 
      label: language === 'fr' ? 'Extras' : 'Extras', 
      icon: Gift 
    },
    'enfants': { 
      label: language === 'fr' ? 'Enfants' : 'Children', 
      icon: Baby 
    },
    'animaux': { 
      label: language === 'fr' ? 'Animaux' : 'Pets', 
      icon: PawPrint 
    },
    'information': { 
      label: language === 'fr' ? 'Informations importantes' : 'Important Information', 
      icon: Info 
    }
  };
  
  const getInfoIcon = (style) => {
    switch(style) {
      case 'warning':
        return AlertTriangle;
      case 'success':
        return CheckCircle;
      case 'important':
        return AlertCircle;
      default:
        return Info;
    }
  };
  
  const getInfoStyles = (style) => {
    switch(style) {
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'important':
        return 'bg-red-50 border-red-200 text-red-900';
      default:
        return 'bg-gray-50 border-gray-400 text-gray-900';
    }
  };
  
  const getIconColor = (style) => {
    switch(style) {
      case 'warning':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      case 'important':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };
  
  const getOptionsTotal = () => {
    let total = 0;
    selectedOptions.forEach(optionId => {
      const option = availableOptions.find(o => o.id === optionId);
      if (option) {
        total += getOptionPrice(option);
      }
    });
    return total;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  
  if (availableOptions.length === 0 && infoModules.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-light mb-6 text-center">
          {t.optionsAndServices}
        </h2>
        <div className="text-center text-gray-600 p-8 bg-gray-50 rounded-lg border border-gray-200">
          <p>{t.noOptionsAvailable}</p>
          <p className="text-sm mt-2">{t.contactForSpecialRequests}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="text-2xl font-light mb-6 text-center">
        {t.optionsAndServices}
      </h2>
      
      <p className="text-center text-gray-600 mb-8">
        {t.personalizeYourStay}
      </p>
      
      {Object.entries(itemsByCategory).map(([category, items]) => {
        const categoryInfo = categoryLabels[category] || { label: category, icon: Package };
        const CategoryIcon = categoryInfo.icon;
        
        return (
          <div key={category} className="mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <CategoryIcon className="w-5 h-5" />
              {categoryInfo.label}
            </h3>
            
            <div className="space-y-4">
              {items.map(item => {
                if (item.type === 'info') {
                  // Toujours afficher l'icône du style dans la carte
                  const IconComponent = getInfoIcon(item.infoStyle);
                  const infoTitle = language === 'fr' ? (item.infoTitleFr || item.infoTitle) : item.infoTitle;
                  const infoContent = language === 'fr' ? (item.infoContentFr || item.infoContent) : item.infoContent;
                  
                  return (
                    <div
                      key={item.id}
                      className={`border rounded-lg p-4 ${getInfoStyles(item.infoStyle)}`}
                    >
                      <div className="flex items-start gap-3">
                        <IconComponent className={`w-5 h-5 mt-0.5 flex-shrink-0 ${getIconColor(item.infoStyle)}`} />
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">
                            {infoTitle}
                          </h4>
                          <p className="text-sm whitespace-pre-wrap">
                            {infoContent}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                const isSelected = selectedOptions.includes(item.id);
                const price = getOptionPrice(item);
                const ItemIcon = getIconComponent(item.iconType);
                const itemName = language === 'fr' ? (item.nameFr || item.name) : item.name;
                const itemDescription = language === 'fr' ? item.descriptionFr : (item.description || item.descriptionFr);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleOption(item.id)}
                    className={`
                      border-2 rounded-lg p-4 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-black bg-gray-50 shadow-lg' 
                        : 'border-gray-300 hover:border-gray-500 hover:shadow-md bg-white'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <ItemIcon className="w-6 h-6 text-gray-700" />
                          <h4 className="text-lg font-semibold">
                            {itemName}
                          </h4>
                        </div>
                        
                        {itemDescription && (
                          <p className="text-sm text-gray-600 mb-2">
                            {itemDescription}
                          </p>
                        )}
                        
                        <p className="text-sm text-gray-500 mb-2">
                          {getOptionDescription(item)}
                        </p>
                        
                        <p className="text-xl font-bold">
                          {t.total}: {price}€
                        </p>
                      </div>
                      
                      <div className="ml-4">
                        <div className={`
                          w-6 h-6 rounded-full border-2 flex items-center justify-center
                          ${isSelected 
                            ? 'border-black bg-black' 
                            : 'border-gray-400 bg-white'
                          }
                        `}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      
      {selectedOptions.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium">{t.optionsTotal}</span>
            <span className="text-2xl font-bold">{getOptionsTotal()}€</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionsSelection;